import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  MapPinIcon,
  WrenchScrewdriverIcon,
  UserGroupIcon,
  CheckCircleIcon,
  ClipboardDocumentListIcon,
  ShieldCheckIcon,
  CalendarDaysIcon,
  StarIcon,
} from "@heroicons/react/24/outline";
import JobEstimateCalculator from "../components/JobEstimateCalculator";

const customerSteps = [
  {
    icon: <MapPinIcon className="h-10 w-10 text-blue-500" />,
    title: "Pick Your Location",
    description:
      "Select your State and LGA to instantly see handymen near you.",
  },
  {
    icon: <WrenchScrewdriverIcon className="h-10 w-10 text-green-500" />,
    title: "Choose a Service",
    description:
      "Browse services like plumbing, electrical, carpentry, and more.",
  },
  {
    icon: <UserGroupIcon className="h-10 w-10 text-purple-500" />,
    title: "Connect with Handymen",
    description:
      "View handyman profiles, ratings, and reviews before choosing.",
  },
  {
    icon: <CheckCircleIcon className="h-10 w-10 text-yellow-500" />,
    title: "Get the Job Done",
    description: "Hire with confidence and pay securely through the platform.",
  },
];

const providerSteps = [
  {
    icon: <ClipboardDocumentListIcon className="h-10 w-10 text-blue-500" />,
    title: "List Your Business",
    description:
      "Create a free profile with your services, pricing and experience.",
  },
  {
    icon: <ShieldCheckIcon className="h-10 w-10 text-green-500" />,
    title: "Get Verified",
    description:
      "Our team reviews your details and marks your profile as verified once approved.",
  },
  {
    icon: <CalendarDaysIcon className="h-10 w-10 text-purple-500" />,
    title: "Receive Bookings",
    description:
      "Customers searching in your state and LGA can find and book you directly.",
  },
  {
    icon: <StarIcon className="h-10 w-10 text-yellow-500" />,
    title: "Build Your Reputation",
    description:
      "Get paid securely and earn reviews that help you win more work.",
  },
];

const HowItWorks: React.FC = () => {
  return (
    <>
      <section className="py-12 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-3">
            How Workman Naija Works
          </h1>
          <p className="text-gray-500 max-w-2xl mx-auto mb-12">
            Whether you need work done or you're a tradesperson looking for
            customers, here's how the platform works for you.
          </p>

          <h2 className="text-2xl font-bold text-gray-800 mb-8">
            For Customers
          </h2>
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4">
            {customerSteps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.15 }}
                viewport={{ once: true }}
                className="bg-white p-6 rounded-2xl shadow-lg"
              >
                <div className="flex justify-center mb-4">{step.icon}</div>
                <h3 className="text-xl font-semibold mb-2">{step.title}</h3>
                <p className="text-gray-600 text-sm">{step.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 bg-white">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-8">
            For Providers
          </h2>
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4">
            {providerSteps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.15 }}
                viewport={{ once: true }}
                className="bg-gray-50 p-6 rounded-2xl shadow-lg"
              >
                <div className="flex justify-center mb-4">{step.icon}</div>
                <h3 className="text-xl font-semibold mb-2">{step.title}</h3>
                <p className="text-gray-600 text-sm">{step.description}</p>
              </motion.div>
            ))}
          </div>
          <Link
            to="/register-provider"
            className="inline-block mt-10 bg-green-700 hover:bg-green-800 text-white font-semibold px-6 py-3 rounded-lg transition"
          >
            List Your Business
          </Link>
        </div>
      </section>

      <section className="py-12 bg-gray-50 px-6">
        <div className="max-w-6xl mx-auto text-center mb-10">
          <h2 className="text-2xl font-bold text-gray-800">
            Not Sure What It'll Cost?
          </h2>
          <p className="text-gray-500 mt-2">
            Get a quick price estimate based on verified providers before you
            book.
          </p>
        </div>
        <JobEstimateCalculator />
      </section>

      <section className="py-12 bg-white px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            Trust &amp; Safety
          </h2>
          <p className="text-gray-600">
            Providers go through a verification review before their profile is
            marked as trusted, and every booking is paid for securely through
            Paystack — we never store your card details. Reviews are only left
            by customers with a completed booking, so ratings reflect real
            experiences.
          </p>
        </div>
      </section>
    </>
  );
};

export default HowItWorks;
